
import { useEffect, useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';
import LocationPicker from '@/components/feature/LocationPicker';

type UserRole = | 'victim' | 'admin' | 'social_worker' | 'supervisor';
type RoleFilter = 'all' | UserRole;
type UserStatus = 'pending' | 'approved' | 'rejected';

interface ManagedUser {
  id: number | string;
  uid?: string;
  name?: string;
  email?: string;
  role: UserRole;
  status?: UserStatus;
  department?: string;
  phone_number?: string;
  profile_img?: string;
  office_name?: string;
  service_municipality?: string;
  service_province?: string;
  office_latitude?: number | string | null;
  office_longitude?: number | string | null;
}

interface UserForm {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  department: string;
  phone_number: string;
  profile_img: string;
  office_name: string;
  service_municipality: string;
  service_province: string;
  office_latitude: string;
  office_longitude: string;
}

interface SelectedLocation {
  latitude: number;
  longitude: number;
  municipality?: string;
  province?: string;
}

const API = 'https://guardiancloudcare.com/backend/api';

const roleLabel: Record<UserRole, string> = {
  admin: 'Administrator',
  social_worker: 'Social Worker',
  supervisor: 'Supervisor',
  victim: 'Victim',
};

const roleBadge: Record<UserRole, string> = {
  admin: 'bg-red-100 text-red-700',
  social_worker: 'bg-teal-100 text-teal-700',
  supervisor: 'bg-amber-100 text-amber-700',
  victim: 'bg-purple-100 text-purple-700',
};

const emptyForm = (): UserForm => ({
  name: '',
  email: '',
  password: '',
  role: 'social_worker',
  department: 'DSWD Region IV-A',
  phone_number: '',
  profile_img: '',
  office_name: '',
  service_municipality: '',
  service_province: 'Laguna',
  office_latitude: '',
  office_longitude: '',
});

const requiresOffice = (role: UserRole) =>
  role === 'supervisor' || role === 'social_worker';

export default function AdminUserManagementPage() {
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [userForm, setUserForm] = useState<UserForm>(emptyForm());
  const [userSearch, setUserSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userSaved, setUserSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [statusPopup, setStatusPopup] = useState<{
    show: boolean;
    type: 'approved' | 'rejected';
    name: string;
  }>({
    show: false,
    type: 'approved',
    name: '',
  });

  const fetchUsers = async () => {
    setLoadingUsers(true);

    try {
      const response = await fetch(`${API}/get_users.php`);
      const data = await response.json();

      if (!response.ok || !Array.isArray(data)) {
        throw new Error(
          data?.error || 'Unable to retrieve the users list.'
        );
      }

      setUsers(data);
      setErrorMessage('');
    } catch (error) {
      console.error('Users fetch error:', error);
      setErrorMessage(
        'Unable to load users. Check Apache, MySQL, and get_users.php.'
      );
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    void fetchUsers();
  }, []);

  const createAuditLog = async (
    action: string,
    description: string,
    targetUid = ''
  ) => {
    try {
      const response = await fetch(`${API}/create_audit_log.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: targetUid,
          user_name: currentUser?.name || '',
          user_role: currentUser?.role || '',
          action,
          module: 'User Management',
          description,
        }),
      });

      if (!response.ok) {
        console.warn('Audit log request failed:', response.status);
      }
    } catch (error) {
      console.error('Audit log error:', error);
    }
  };

  const filteredUsers = users.filter((user) => {
    const search = userSearch.trim().toLowerCase();
    const name = (user.name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();

    return (
      (name.includes(search) || email.includes(search)) &&
      (roleFilter === 'all' || user.role === roleFilter)
    );
  });

  const admins = filteredUsers.filter((u) => u.role === 'admin');
  const socialWorkers = filteredUsers.filter(
    (u) => u.role === 'social_worker'
  );
  const supervisors = filteredUsers.filter(
    (u) => u.role === 'supervisor'
  );
  const victims = filteredUsers.filter((u) => u.role === 'victim');

  const setFormField = <K extends keyof UserForm>(
    field: K,
    value: UserForm[K]
  ) => {
    setUserForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const openNewUser = () => {
    setEditUserId(null);
    setUserForm(emptyForm());
    setShowPassword(false);
    setErrorMessage('');
    setShowUserModal(true);
  };

  const openEditUser = (user: ManagedUser) => {
    setEditUserId(String(user.id));
    setShowPassword(false);
    setErrorMessage('');

    setUserForm({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role,
      department: user.department || '',
      phone_number: user.phone_number || '',
      profile_img: user.profile_img || '',
      office_name: user.office_name || '',
      service_municipality: user.service_municipality || '',
      service_province: user.service_province || 'Laguna',
      office_latitude:
        user.office_latitude == null
          ? ''
          : String(user.office_latitude),
      office_longitude:
        user.office_longitude == null
          ? ''
          : String(user.office_longitude),
    });

    setShowUserModal(true);
  };

  const validateUserForm = (): boolean => {
    if (!userForm.name.trim()) {
      alert('Please enter the user full name.');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(userForm.email.trim())) {
      alert('Please enter a valid email address.');
      return false;
    }

    if (!editUserId && userForm.password.length < 8) {
      alert('Password must be at least 8 characters.');
      return false;
    }

    if (editUserId && userForm.password &&
        userForm.password.length < 8) {
      alert('Password must be at least 8 characters.');
      return false;
    }

    if (userForm.phone_number.trim() &&
        !/^09\d{9}$/.test(userForm.phone_number.trim())) {
      alert('Enter a valid Philippine mobile number (09XXXXXXXXX).');
      return false;
    }

    if (requiresOffice(userForm.role)) {
      if (!userForm.office_name.trim()) {
        alert('Please enter the office name.');
        return false;
      }

      if (
        !userForm.service_municipality.trim() ||
        !userForm.service_province.trim()
      ) {
        alert('Please enter the office municipality and province.');
        return false;
      }

      const lat = Number(userForm.office_latitude);
      const lng = Number(userForm.office_longitude);

      if (
        userForm.office_latitude.trim() === '' ||
        userForm.office_longitude.trim() === '' ||
        !Number.isFinite(lat) ||
        !Number.isFinite(lng) ||
        lat < -90 ||
        lat > 90 ||
        lng < -180 ||
        lng > 180
      ) {
        alert('Please select an office location on the map.');
        return false;
      }
    }

    return true;
  };

  const handleSaveUser = async () => {
    if (saving || !validateUserForm()) return;

    setSaving(true);
    setErrorMessage('');

    try {
      const isEditing = editUserId !== null;
      const endpoint = isEditing
        ? `${API}/update_user.php`
        : `${API}/add_user.php`;

      const payload = {
        ...(isEditing ? { id: editUserId } : {}),
        ...userForm,
        name: userForm.name.trim(),
        email: userForm.email.trim(),
        phone_number: userForm.phone_number.trim(),
        office_name: requiresOffice(userForm.role)
          ? userForm.office_name.trim()
          : '',
        service_municipality: requiresOffice(userForm.role)
          ? userForm.service_municipality.trim()
          : '',
        service_province: requiresOffice(userForm.role)
          ? userForm.service_province.trim()
          : '',
        office_latitude: requiresOffice(userForm.role)
          ? userForm.office_latitude
          : '',
        office_longitude: requiresOffice(userForm.role)
          ? userForm.office_longitude
          : '',
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to save user.');
      }

      const existingUser = users.find(
        (u) => String(u.id) === String(editUserId)
      );

      const targetUid = isEditing
        ? existingUser?.uid || ''
        : String(data.user?.uid || data.uid || '');

      await createAuditLog(
        isEditing ? 'Update' : 'Create',
        `${isEditing ? 'Updated' : 'Created'} user account for ${userForm.name} (${userForm.role})`,
        targetUid
      );

      setShowUserModal(false);
      setUserSaved(true);
      await fetchUsers();

      window.setTimeout(() => setUserSaved(false), 2500);
    } catch (error) {
      console.error('Save user error:', error);
      setErrorMessage(
        error instanceof Error ? error.message : 'Unable to save user.'
      );
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (
    id: number,
    status: 'approved' | 'rejected',
    name: string
  ) => {
    try {
      const response = await fetch(`${API}/update_user_status.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        alert(data.error || 'Failed to update user status.');
        return;
      }

      const target = users.find((u) => Number(u.id) === id);

      setUsers((previous) =>
        previous.map((u) =>
          Number(u.id) === id ? { ...u, status } : u
        )
      );

      await createAuditLog(
        status === 'approved' ? 'Approve' : 'Reject',
        `${status === 'approved' ? 'Approved' : 'Rejected'} user account for ${name}`,
        target?.uid || ''
      );

      setStatusPopup({ show: true, type: status, name });

      window.setTimeout(() => {
        setStatusPopup({
          show: false,
          type: 'approved',
          name: '',
        });
      }, 2500);
    } catch (error) {
      console.error('Update status error:', error);
      alert('Unable to connect to the server.');
    }
  };

  const deleteUser = async (id: string) => {
    const target = users.find((u) => String(u.id) === String(id));

    if (!target) {
      alert('User not found.');
      return;
    }

    if (currentUser?.uid && target.uid === currentUser.uid) {
      alert('You cannot delete your own account.');
      setDeleteConfirm(null);
      return;
    }

    try {
      const response = await fetch(`${API}/delete_user.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        alert(data.error || 'Failed to delete user.');
        return;
      }

      setUsers((previous) =>
        previous.filter((u) => String(u.id) !== String(id))
      );

      await createAuditLog(
        'Delete',
        `Deleted user account for ${target.name || 'Unknown User'} (${target.role})`,
        target.uid || ''
      );

      setDeleteConfirm(null);
    } catch (error) {
      console.error('Delete user error:', error);
      alert('Unable to connect to the server.');
    }
  };

  const handleMapSelection = (location: SelectedLocation) => {
    setUserForm((previous) => ({
      ...previous,
      office_latitude: String(location.latitude),
      office_longitude: String(location.longitude),
      service_municipality:
        location.municipality || previous.service_municipality,
      service_province:
        location.province || previous.service_province,
    }));
  };

  return (
    <AppLayout
      title="User Management"
      breadcrumb={['Admin', 'User Management']}
    >
      {statusPopup.show && (
        <div className="fixed right-5 top-5 z-[100] rounded-xl border bg-white p-4 shadow-xl">
          <p className="font-semibold">
            {statusPopup.type === 'approved'
              ? 'User Approved'
              : 'User Rejected'}
          </p>
          <p className="text-sm text-gray-600">
            {statusPopup.name}{' '}
            {statusPopup.type === 'approved'
              ? 'has been approved.'
              : 'has been rejected.'}
          </p>
        </div>
      )}

      {errorMessage && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <span>{errorMessage}</span>
          <button
            type="button"
            onClick={() => setErrorMessage('')}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(['admin', 'social_worker', 'supervisor', 'victim'] as const).map(
          (role) => (
            <div
              key={role}
              className="rounded-xl border border-gray-100 bg-white p-5 text-center"
            >
              <p className="text-3xl font-bold text-gray-900">
                {users.filter((u) => u.role === role).length}
              </p>
              <span
                className={`rounded-full px-2 py-1 text-xs font-medium ${roleBadge[role]}`}
              >
                {role === 'admin'
                  ? 'Administrators'
                  : role === 'social_worker'
                    ? 'Social Workers'
                    : roleLabel[role] + 's'}
              </span>
            </div>
          )
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Search users..."
            className="min-w-48 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-teal-500"
          />

          <select
            value={roleFilter}
            onChange={(e) =>
              setRoleFilter(e.target.value as RoleFilter)
            }
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-teal-500"
          >
            <option value="all">All roles</option>
            <option value="admin">Administrators</option>
            <option value="social_worker">Social Workers</option>
            <option value="supervisor">Supervisors</option>
            <option value="victim">Victims</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          {loadingUsers && (
            <span className="text-sm text-gray-500">Loading users...</span>
          )}

          {userSaved && (
            <span className="text-sm font-medium text-teal-700">
              Saved!
            </span>
          )}

          <button
            type="button"
            onClick={openNewUser}
            className="rounded-xl bg-teal-500 px-5 py-2.5 text-sm text-white hover:bg-teal-600"
          >
            + Add User
          </button>
        </div>
      </div>

      <UserTable
        title="Administrators"
        users={admins}
        openEditUser={openEditUser}
        setDeleteConfirm={setDeleteConfirm}
        updateStatus={updateStatus}
      />

      <UserTable
        title="Social Workers"
        users={socialWorkers}
        openEditUser={openEditUser}
        setDeleteConfirm={setDeleteConfirm}
        updateStatus={updateStatus}
      />

      <UserTable
        title="Supervisors"
        users={supervisors}
        openEditUser={openEditUser}
        setDeleteConfirm={setDeleteConfirm}
        updateStatus={updateStatus}
      />

      <UserTable
        title="Victims"
        users={victims}
        openEditUser={openEditUser}
        setDeleteConfirm={setDeleteConfirm}
        updateStatus={updateStatus}
      />

      {showUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {editUserId ? 'Edit User' : 'Add New User'}
              </h3>
              <button
                type="button"
                onClick={() => setShowUserModal(false)}
                disabled={saving}
                className="text-xl text-gray-400 hover:text-gray-700"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  Full Name
                </label>
                <input
                  value={userForm.name}
                  onChange={(e) => setFormField('name', e.target.value)}
                  placeholder="Full name"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  Email
                </label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setFormField('email', e.target.value)}
                  placeholder="email@dswd.gov.ph"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  Password
                  {editUserId && ' (leave blank to keep current password)'}
                </label>
                <div className="flex gap-2">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={userForm.password}
                    onChange={(e) =>
                      setFormField('password', e.target.value)
                    }
                    placeholder="At least 8 characters"
                    className="min-w-0 flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="rounded-xl border px-3 text-sm"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium">
                  Role
                </label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {(
                    [ 'victim', 'admin', 'social_worker', 'supervisor'] as const
                  ).map((role) => (
                    <button
                      type="button"
                      key={role}
                      onClick={() => {
                        setUserForm((previous) => ({
                          ...previous,
                          role,
                          department:
                            role === 'victim'
                              ? ''
                              : previous.department || 'DSWD Region IV-A',
                          // Keep office details when switching between
                          // Social Worker and Supervisor.
                          office_name: requiresOffice(role)
                            ? previous.office_name
                            : '',
                          service_municipality: requiresOffice(role)
                            ? previous.service_municipality
                            : '',
                          service_province: requiresOffice(role)
                            ? previous.service_province
                            : 'Laguna',
                          office_latitude: requiresOffice(role)
                            ? previous.office_latitude
                            : '',
                          office_longitude: requiresOffice(role)
                            ? previous.office_longitude
                            : '',
                        }));
                      }}
                      className={`rounded-xl border-2 px-2 py-2.5 text-xs font-medium ${
                        userForm.role === role
                          ? 'border-teal-500 bg-teal-50 text-teal-700'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {roleLabel[role]}
                    </button>
                  ))}
                </div>
              </div>

              {userForm.role !== 'victim' && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium">
                    Department
                  </label>
                  <input
                    value={userForm.department}
                    onChange={(e) =>
                      setFormField('department', e.target.value)
                    }
                    placeholder="DSWD Region IV-A"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                  />
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-sm font-medium">
                  Phone Number
                </label>
                <input
                  value={userForm.phone_number}
                  onChange={(e) =>
                    setFormField('phone_number', e.target.value)
                  }
                  placeholder="09XXXXXXXXX"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                />
              </div>

              {requiresOffice(userForm.role) && (
                <section className="space-y-4 border-t border-gray-200 pt-4">
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      {userForm.role === 'supervisor'
                        ? 'Supervisor Office Location'
                        : 'Social Worker Office Location'}
                    </h4>
                    <p className="mt-1 text-sm text-gray-500">
                      Select the DSWD office location on the map.
                      Check the office name and coordinates before saving.
                    </p>
                  </div>

                  <LocationPicker
                    onLocationSelect={handleMapSelection}
                  />

                  <div>
                    <label className="mb-1.5 block text-sm font-medium">
                      Office Name
                    </label>
                    <input
                      value={userForm.office_name}
                      onChange={(e) =>
                        setFormField('office_name', e.target.value)
                      }
                      placeholder="e.g. DSWD Paete"
                      className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Municipality
                      </label>
                      <input
                        value={userForm.service_municipality}
                        onChange={(e) =>
                          setFormField(
                            'service_municipality',
                            e.target.value
                          )
                        }
                        placeholder="Paete"
                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Province
                      </label>
                      <input
                        value={userForm.service_province}
                        onChange={(e) =>
                          setFormField('service_province', e.target.value)
                        }
                        placeholder="Laguna"
                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Latitude
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={userForm.office_latitude}
                        onChange={(e) =>
                          setFormField('office_latitude', e.target.value)
                        }
                        placeholder="14.3641000"
                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium">
                        Longitude
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={userForm.office_longitude}
                        onChange={(e) =>
                          setFormField('office_longitude', e.target.value)
                        }
                        placeholder="121.4816000"
                        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-teal-500"
                      />
                    </div>
                  </div>
                </section>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowUserModal(false)}
                disabled={saving}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSaveUser()}
                disabled={
                  saving ||
                  !userForm.name.trim() ||
                  !userForm.email.trim()
                }
                className="rounded-xl bg-teal-500 px-6 py-2 text-sm text-white hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving
                  ? 'Saving...'
                  : editUserId
                    ? 'Save Changes'
                    : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-2xl text-red-600">
              !
            </div>
            <h3 className="mb-2 text-lg font-bold text-gray-900">
              Delete User?
            </h3>
            <p className="mb-5 text-sm text-gray-500">
              This action cannot be undone. The user will lose access if
              the server successfully deletes the account.
            </p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void deleteUser(deleteConfirm)}
                className="rounded-xl bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

interface UserTableProps {
  title: string;
  users: ManagedUser[];
  openEditUser: (user: ManagedUser) => void;
  setDeleteConfirm: (id: string | null) => void;
  updateStatus: (
    id: number,
    status: 'approved' | 'rejected',
    name: string
  ) => void | Promise<void>;
}

function UserTable({
  title,
  users,
  openEditUser,
  setDeleteConfirm,
  updateStatus,
}: UserTableProps) {
  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-gray-100 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <h3 className="font-bold text-gray-800">{title}</h3>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">
          {users.length} user{users.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-5 py-3 font-medium">User</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Role</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="hidden px-5 py-3 font-medium md:table-cell">
                Department / Office
              </th>
              <th className="px-5 py-3 font-medium">Permissions</th>
              <th className="px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50">
            {users.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-5 py-8 text-center text-sm text-gray-400"
                >
                  No {title.toLowerCase()} found.
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr
                  key={user.id}
                  className="transition-colors hover:bg-gray-50"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-teal-500 text-xs font-bold text-white">
                        {user.profile_img ? (
                          <img
                            src={`${API.replace('/api', '')}/uploads/${user.profile_img}`}
                            alt={user.name || 'User profile'}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          (user.name || 'U').charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {user.name || 'No Name'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {user.phone_number || 'No phone'}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="px-5 py-3 text-sm text-gray-600">
                    {user.email || '-'}
                  </td>

                  <td className="px-5 py-3">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${roleBadge[user.role]}`}
                    >
                      {roleLabel[user.role]}
                    </span>
                  </td>

                  <td className="px-5 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          user.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : user.status === 'rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {user.status || 'pending'}
                      </span>

                      {user.status === 'pending' && (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              void updateStatus(
                                Number(user.id),
                                'approved',
                                user.name || 'Unknown User'
                              )
                            }
                            className="rounded-lg bg-green-100 px-2 py-1 text-xs text-green-700"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              void updateStatus(
                                Number(user.id),
                                'rejected',
                                user.name || 'Unknown User'
                              )
                            }
                            className="rounded-lg bg-red-100 px-2 py-1 text-xs text-red-700"
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>

                  <td className="hidden px-5 py-3 text-sm text-gray-500 md:table-cell">
                    {requiresOffice(user.role)
                      ? [
                          user.office_name,
                          user.service_municipality,
                          user.service_province,
                        ]
                          .filter(Boolean)
                          .join(', ') || user.department || '-'
                      : user.department || '-'}
                  </td>

                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(
                        {
                          admin: ['Full Access', 'AI Config', 'Legal KB'],
                          social_worker: ['My Cases', 'AI Tools', 'Submit'],
                          supervisor: [
                            'Review Reports',
                            'Assign Cases',
                            'Notifications',
                          ],
                          victim: ['Dashboard', 'Report', 'Law', 'Resources'],
                        } as Record<UserRole, string[]>
                      )[user.role].map((permission) => (
                        <span
                          key={permission}
                          className={`rounded px-1.5 py-0.5 text-xs ${
                            user.role === 'admin'
                              ? 'bg-red-50 text-red-600'
                              : user.role === 'victim'
                                ? 'bg-purple-50 text-purple-600'
                                : user.role === 'supervisor'
                                  ? 'bg-amber-50 text-amber-700'
                                  : 'bg-teal-50 text-teal-600'
                          }`}
                        >
                          {permission}
                        </span>
                      ))}
                    </div>
                  </td>

                  <td className="px-5 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEditUser(user)}
                        className="flex items-center gap-1 whitespace-nowrap rounded-lg bg-gray-100 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-200"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteConfirm(String(user.id))}
                        className="flex items-center gap-1 whitespace-nowrap rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-600 hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
